import LegalLayout from './LegalLayout';

const P = ({ children }: { children: React.ReactNode }) => (
  <span className="legal-placeholder">{children}</span>
);

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="2026-06-18">
      <p>
        RentNova ("we", "us", "our") respects your privacy and is committed to
        protecting your personal data. This Privacy Policy explains how we
        collect, use, store, and disclose your personal data when you use
        rentnova.app and the related RentNova services (the "Service").
      </p>
      <p>
        This policy is written to comply with the EU General Data Protection
        Regulation (Regulation (EU) 2016/679, "GDPR") and the Slovak Act
        No. 18/2018 Coll. on the Protection of Personal Data.
      </p>

      <h2>1. Who we are (Data Controller)</h2>
      <p>
        The data controller responsible for your personal data is:
      </p>
      <ul>
        <li><strong>Company:</strong> <P>[YOUR LEGAL COMPANY NAME, s.r.o.]</P></li>
        <li><strong>Registered office:</strong> <P>[STREET, POSTAL CODE, CITY, COUNTRY]</P></li>
        <li><strong>Company registration no. (IČO):</strong> <P>[IČO]</P></li>
        <li><strong>VAT ID (DIČ / IČ DPH):</strong> <P>[DIČ]</P></li>
        <li><strong>Registered in:</strong> <P>[COMMERCIAL REGISTER, SECTION, INSERT NO.]</P></li>
        <li><strong>Contact:</strong> <a href="mailto:contact@rentnova.app">contact@rentnova.app</a></li>
      </ul>

      <h2>2. What data we collect</h2>

      <h3>2.1 Data you provide directly</h3>
      <ul>
        <li><strong>Account data:</strong> name, email address, password (hashed), role (landlord or tenant).</li>
        <li><strong>Property data (landlords):</strong> property addresses, descriptions, photos, lease terms.</li>
        <li><strong>Tenant data (landlords entering tenant info):</strong> tenant name, email, phone, lease dates, rent amounts.</li>
        <li><strong>Maintenance data:</strong> ticket descriptions, optional photos.</li>
        <li><strong>Documents:</strong> uploaded PDFs (leases, invoices), digital signatures (image data and timestamp).</li>
        <li><strong>Chat messages:</strong> message content and attachments exchanged between landlords and tenants.</li>
        <li><strong>Payment data:</strong> we do <strong>not</strong> store card numbers; payments are handled by Stripe, Inc. We store only the Stripe customer/subscription identifier and last-4 / brand metadata returned by Stripe.</li>
        <li><strong>Communication:</strong> emails you send us at contact@rentnova.app.</li>
      </ul>

      <h3>2.2 Data collected automatically</h3>
      <ul>
        <li><strong>Technical data:</strong> IP address (hashed for short retention), browser type, device type, OS, language.</li>
        <li><strong>Usage data:</strong> pages visited, features used, timestamps. Used to keep the Service reliable.</li>
        <li><strong>Error reports:</strong> if a runtime error occurs, we collect a stack trace via Sentry (does not include form values or passwords).</li>
        <li><strong>Cookies and similar:</strong> see our <a href="/cookies">Cookie Policy</a>.</li>
      </ul>

      <h3>2.3 Data from third parties</h3>
      <ul>
        <li><strong>Stripe:</strong> when you pay, Stripe sends us webhooks containing your customer ID, subscription status, and the price you bought (so we know your plan).</li>
      </ul>

      <h2>3. How we use your data (legal bases)</h2>
      <table>
        <thead>
          <tr><th>Purpose</th><th>Legal basis (GDPR Art. 6)</th></tr>
        </thead>
        <tbody>
          <tr><td>Providing the Service (account, properties, payments)</td><td>Contract (6(1)(b))</td></tr>
          <tr><td>Sending transactional emails (sign-up confirmation, password reset, invoices)</td><td>Contract (6(1)(b))</td></tr>
          <tr><td>Processing payments via Stripe</td><td>Contract (6(1)(b))</td></tr>
          <tr><td>Security, fraud prevention, logging</td><td>Legitimate interest (6(1)(f))</td></tr>
          <tr><td>Accounting / tax compliance</td><td>Legal obligation (6(1)(c))</td></tr>
          <tr><td>Product analytics (privacy-friendly, no profiling)</td><td>Legitimate interest (6(1)(f)) — opt-out available</td></tr>
          <tr><td>Marketing emails (newsletters), if you opt in</td><td>Consent (6(1)(a))</td></tr>
        </tbody>
      </table>

      <h2>4. Who we share your data with</h2>
      <p>
        We do <strong>not</strong> sell your personal data. We share it only with the following
        processors, each under a data processing agreement (DPA):
      </p>
      <ul>
        <li><strong>Supabase, Inc.</strong> — hosted database and authentication. Servers located in the EU (eu-west-1).</li>
        <li><strong>Vercel, Inc.</strong> — web hosting and edge functions. Region-locked to the EU where supported.</li>
        <li><strong>Stripe Payments Europe, Ltd.</strong> — payment processing.</li>
        <li><strong>Sentry (Functional Software, Inc.)</strong> — runtime error monitoring.</li>
        <li><strong>Resend, Inc.</strong> (or other configured SMTP) — transactional email delivery.</li>
      </ul>
      <p>
        Where any processor is located outside the EU/EEA, transfers rely on
        Standard Contractual Clauses (SCC) approved by the European Commission.
      </p>

      <h2>5. How long we keep your data</h2>
      <ul>
        <li><strong>Account data:</strong> for as long as your account exists. Deleted within 30 days of account deletion (some logs may persist up to 90 days for security/abuse investigations).</li>
        <li><strong>Property, tenant, payment, document data:</strong> for the duration of your subscription and 24 months after, then permanently deleted. Some records (invoices) are retained for the statutory 10-year accounting period under Slovak law.</li>
        <li><strong>Chat messages:</strong> while your account exists or until you delete them.</li>
        <li><strong>Error logs:</strong> 90 days.</li>
      </ul>

      <h2>6. Your rights under GDPR</h2>
      <p>You can exercise the following rights at any time:</p>
      <ul>
        <li><strong>Right of access (Art. 15):</strong> request a copy of all data we hold on you. Available in-app under <em>Settings → Privacy → Export my data</em>.</li>
        <li><strong>Right to rectification (Art. 16):</strong> correct inaccurate data. Edit your profile or contact us.</li>
        <li><strong>Right to erasure / "right to be forgotten" (Art. 17):</strong> delete your account and associated data. Available in-app under <em>Settings → Privacy → Delete account</em>.</li>
        <li><strong>Right to restriction of processing (Art. 18):</strong> ask us to temporarily stop using your data.</li>
        <li><strong>Right to data portability (Art. 20):</strong> receive your data in a machine-readable format (JSON export).</li>
        <li><strong>Right to object (Art. 21):</strong> object to processing based on legitimate interest.</li>
        <li><strong>Right to withdraw consent (Art. 7):</strong> withdraw consent for processing that was based on consent (e.g., marketing emails) at any time.</li>
        <li><strong>Right to lodge a complaint:</strong> with your supervisory authority. In Slovakia, that is the <a href="https://dataprotection.gov.sk/" target="_blank" rel="noreferrer">Úrad na ochranu osobných údajov SR</a>.</li>
      </ul>
      <p>
        To exercise any of these rights, email <a href="mailto:contact@rentnova.app">contact@rentnova.app</a>.
        We respond within 30 days.
      </p>

      <h2>7. Security</h2>
      <p>
        We protect your data with industry-standard measures: TLS encryption in
        transit, encrypted storage at rest, row-level security in our database
        so users can only see their own data, hashed passwords, audit logs,
        leaked-password protection for new sign-ups, and continuous security
        advisor checks.
      </p>
      <p>
        We do not have access to your raw payment card data — that is handled
        exclusively by Stripe, who is PCI-DSS Level 1 certified.
      </p>

      <h2>8. Children</h2>
      <p>
        RentNova is not directed at children under 16. We do not knowingly
        collect data from children. If you believe we have, contact us and we
        will delete it.
      </p>

      <h2>9. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be
        announced by email to account holders at least 14 days before they
        take effect. The "Last updated" date at the top reflects the most
        recent revision.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions about this policy or your data:{' '}
        <a href="mailto:contact@rentnova.app">contact@rentnova.app</a>.
      </p>
    </LegalLayout>
  );
}
